import {
  Box,
  BoxProps,
  Circle,
  createIcon,
  Icon,
  Stack,
  StackProps,
  Grid,
  GridItem,
  useId,
  useRadio,
  useRadioGroup,
  UseRadioProps,
  useStyleConfig,
  GridProps,
} from "@chakra-ui/react";
import React from "react";

interface RadioCardGroupProps<T> extends Omit<StackProps, "onChange"> {
  name?: string;
  value?: T;
  defaultValue?: string;
  onChange?: (value: T) => void;
}

export const RadioCardGroup = <T extends string>(
  props: RadioCardGroupProps<T>
) => {
  const { children, name, defaultValue, value, onChange, ...rest } = props;
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    defaultValue,
    value,
    onChange,
  });
  const cards = React.useMemo(
    () =>
      React.Children.toArray(children)
        .filter<React.ReactElement<RadioCardProps>>(React.isValidElement)
        .map((card) => {
          return React.cloneElement(card, {
            radioProps: getRadioProps({
              value: card.props.value,
            }),
          });
        }),
    [children, getRadioProps]
  );

  return <Stack {...getRootProps(rest)}>{cards}</Stack>;
};

interface RadioCardGroupGridProps<T> extends Omit<GridProps, "onChange"> {
  name?: string;
  value?: T;
  defaultValue?: string;
  onChange?: (value: T) => void;
}

export const RadioCardGroupGrid = <T extends string>(
  props: RadioCardGroupGridProps<T>
) => {
  const { children, name, defaultValue, value, onChange, ...rest } = props;
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    defaultValue,
    value,
    onChange,
  });
  const cards = React.useMemo(
    () =>
      React.Children.toArray(children)
        .filter<React.ReactElement<RadioCardProps>>(React.isValidElement)
        .map((card) => {
          return (
            <GridItem w="100%" h="20">
              {React.cloneElement(card, {
                radioProps: getRadioProps({
                  value: card.props.value,
                }),
              })}
            </GridItem>
          );
        }),
    [children, getRadioProps]
  );

  return (
    <Grid templateColumns="repeat(2, 1fr)" gap={6} {...getRootProps(rest)}>
      {cards}
    </Grid>
  );
};

interface RadioCardProps extends BoxProps {
  value: string;
  radioProps?: UseRadioProps;
  gridMode?: boolean;
}

export const RadioCard = (props: RadioCardProps) => {
  const { gridMode, radioProps, children, ...rest } = props;
  const { getInputProps, getCheckboxProps, getLabelProps, state } =
    useRadio(radioProps);
  const id = useId(undefined, "radio-button");

  const styles = useStyleConfig("RadioCard", props);
  const inputProps = getInputProps();
  const checkboxProps = getCheckboxProps();
  const labelProps = getLabelProps();
  return (
    <Box
      as="label"
      cursor="pointer"
      {...labelProps}
      sx={{
        ".focus-visible + [data-focus]": {
          boxShadow: "outline",
          zIndex: 1,
        },
      }}
    >
      <input {...inputProps} aria-labelledby={id} />
      <Box sx={styles} {...checkboxProps} {...rest}>
        <Stack direction="row">
          <Box flex="1">{children}</Box>
          {!gridMode ? (
            <>
              {state.isChecked ? (
                <Circle bg="accent" size="4">
                  <Icon as={CheckIcon} boxSize="2.5" color="inverted" />
                </Circle>
              ) : (
                <Circle borderWidth="2px" size="4" />
              )}
            </>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
};

const CheckIcon = createIcon({
  displayName: "CheckIcon",
  viewBox: "0 0 12 10",
  path: (
    <polyline
      fill="none"
      strokeWidth="2px"
      stroke="currentColor"
      strokeDasharray="16px"
      points="1.5 6 4.5 9 10.5 1"
    />
  ),
});
